// @ts-nocheck
import _ from 'lodash';
import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, Box, Tabs, Tab } from '@material-ui/core';
import {
  useAlert,
  useHistoryState,
  useIfMounted,
  useOnMount,
  useOnPendingActionFinish,
} from '../../../../../common/hooks';
import { AlertSeverity } from '../../../../../common/providers';
import { AppRoute } from '../../../../app';
import { UserService } from '../../../../customers';
import { EmptyState, LoadingWrapper, Panel, TabPanel } from '../../../../../common/components';
import { selectPendingActions } from '../../../../../common/state/pending';
import {
  activateContract,
  addUsersToContract,
  closeContract,
  fetchContract,
} from '../../../state/companyActions';
import { selectContractDetails } from '../../../state/companySelectors';
import ContractDetailsTab from './partials/ContractDetailsTab';
import ContractUsersTab from './partials/ContractUsersTab';
import {
  ACTIVATE_CONTRACT,
  ADD_USERS_TO_CONTRACT,
  CLOSE_CONTRACT,
} from '../../../state/companyActionTypes';
import { SubscriptionService } from '../../../../subscriptions';
import { response } from 'msw';

const TabIndex = {
  Details: 0,
  Users: 1,
};

const useStyles = makeStyles(theme => ({
  root: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  panel: {
    paddingTop: '68px',
    paddingLeft: '0',
    paddingRight: '0',
    paddingBottom: '0',
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
  fullHeight: {
    flex: '1',
    height: '100%',
  },
}));

function ContractDetailsPage() {
  const [loading, setLoading] = useHistoryState('loading', true);
  const [tabIndex, setTabIndex] = useHistoryState('tabIndex', TabIndex.Details);
  const [t] = useTranslation();
  const { showAlert } = useAlert();
  const { companyId, contractId } = useParams();
  const history = useHistory();
  const contractDetails = useSelector(selectContractDetails);
  const pendingActions = useSelector(selectPendingActions);
  const classes = useStyles();
  const ifMounted = useIfMounted();
  const dispatch = useDispatch();

  useOnMount(() => {
    async function handleFetchData() {
      try {
        if (!contractDetails) {
          setLoading(true);
        }
        await dispatch(fetchContract(contractId));
      } catch (e) {
        showAlert({
          title: t('contract.contractDetailsFetchError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      } finally {
        ifMounted(() => setLoading(false));
      }
    }
    handleFetchData();
  });

  useOnPendingActionFinish([ACTIVATE_CONTRACT, ADD_USERS_TO_CONTRACT, CLOSE_CONTRACT], async () => {
    try {
      setLoading(true);
      await dispatch(fetchContract(contractId));
    } finally {
      setLoading(false);
    }
  });

  const handleActivateContract = useCallback(async () => {
    try {
      await dispatch(activateContract(companyId, contractId));
      showAlert({
        title: t('common.success'),
        message: t('contract.contractActivateSuccess'),
        severity: AlertSeverity.Success,
      });
    } catch (e) {
      showAlert({
        title: t('contract.contractActivateError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [companyId, contractId, dispatch, showAlert, t]);

  const handleContractUsers = useCallback(
    async userIds => {
      try {
        setLoading(true);
        await dispatch(addUsersToContract(companyId, contractId, userIds));
        showAlert({
          title: t('common.success'),
          message: t('contract.contractUpdateUsersSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('contract.contractUpdateUsersError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      } finally {
        setLoading(false);
      }
    },
    [companyId, contractId, dispatch, showAlert, t]
  );

  const handleCloseContract = useCallback(
    async force => {
      try {
        await dispatch(closeContract(contractId, force));
        showAlert({
          title: t('common.success'),
          message: t('contract.contractCloseSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('contract.contractCloseError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      }
    },
    [contractId, dispatch, showAlert, t]
  );

  const handleAddUser = useCallback(
    async email => {
      try {
        const { data } = await UserService.fetchUserByEmail(email);
        const checkResults = await Promise.all(data.users.map(async user => await handleCheckIfUserHasContract(user.userId)));
        return {
          email: email,
          response: data,
          isAllowed: !checkResults.every(check => check)
        };
      } catch (e) {
        showAlert({
          title: t('contract.contractAddUserError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      }
    },
    [showAlert, t]
  );

  const handleCheckIfUserHasContract = 
    async userId => {
      try {
        const response = await SubscriptionService.checkIfUserCanBeAddedToContract(userId);
        return response.status !== 200;
      } catch (e) {
        console.log(e);
        showAlert({
          title: t('contract.contractAddUserError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        return true;
      }
  };

  const handleGoBack = useCallback(() => {
    history.push(
      _.defaultTo(
        history.location.state?.from,
        AppRoute.CompanyDetails.replace(':companyId', companyId)
      )
    );
  }, [companyId, history]);

  return (
    <Box id="ContractDetails-container" className={classes.root}>
      <LoadingWrapper loading={loading}>
        <Panel
          id="ContractDetails-panel"
          title={t('contract.contractDetails')}
          className={clsx(classes.panel, {
            [classes.fullHeight]: tabIndex !== TabIndex.Details,
          })}
        >
          {!contractDetails ? (
            <EmptyState variant="unavailable" message={t('contract.contractDetailsNotAvailable')} />
          ) : (
            <>
              <Tabs
                id="ContractDetails-tabs"
                className={classes.tabs}
                data-value={tabIndex}
                value={tabIndex}
                indicatorColor="primary"
                textColor="primary"
                onChange={(_event, index) => setTabIndex(index)}
              >
                <Tab
                  id="ContractDetails-detailsTab"
                  label={t('contract.contract')}
                  data-index={TabIndex.Details}
                />
                <Tab
                  id="ContractDetails-usersTab"
                  label={t('contract.contractUsers')}
                  data-index={TabIndex.Users}
                />
              </Tabs>
              <Box id="ContractDetails-tabsContent" className={classes.fullHeight}>
                <TabPanel index={TabIndex.Details} currentIndex={tabIndex}>
                  <ContractDetailsTab
                    contract={contractDetails}
                    onCloseContract={handleCloseContract}
                    onGoBack={handleGoBack}
                    actionsDisabled={!_.isEmpty(pendingActions)}
                  />
                </TabPanel>
                <TabPanel index={TabIndex.Users} currentIndex={tabIndex}>
                  <ContractUsersTab
                    contract={contractDetails}
                    onAddUser={handleAddUser}
                    onActivateContract={handleActivateContract}
                    onUpdateContractUsers={handleContractUsers}
                    actionsDisabled={!_.isEmpty(pendingActions)}
                  />
                </TabPanel>
              </Box>
            </>
          )}
        </Panel>
      </LoadingWrapper>
    </Box>
  );
}

export default ContractDetailsPage;
